<script setup lang="ts">
import {
  UserButton,
  SignInButton,
  SignUpButton,
  useAuth,
  useUser,
} from "@clerk/vue";
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import LucideIcon from "@/components/LucideIcon.vue";
import ThemeToggle from "@/components/ThemeToggle.vue";
import { Button } from "@/components/ui/button";
import { PandoSheet } from "@/components/ui/sheet";
import {
  useNavigation,
  type NavigationItem,
} from "@/composables/useNavigation";

defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
}>();

const route = useRoute();
const router = useRouter();
const auth = useAuth();
const { user } = useUser();
const { navigationItems } = useNavigation();

// Track expanded sections
const expandedSections = ref<Set<string>>(new Set());

// Group navigation items by section
const groupedNavigationItems = computed(() => {
  const groups: Array<{ section: string | null; items: NavigationItem[] }> = [];
  let currentSection: string | null = null;

  for (const item of navigationItems.value) {
    const itemSection = item.section ?? null;
    if (itemSection !== currentSection) {
      currentSection = itemSection;
      groups.push({ section: currentSection, items: [] });
    }
    groups[groups.length - 1].items.push(item);
  }

  return groups;
});

const isActive = (path: string) => {
  return route.path === path || route.path.startsWith(path + "/");
};

const isChildActive = (path: string) => {
  return route.path === path;
};

const isExpanded = (path: string): boolean => {
  return expandedSections.value.has(path);
};

const toggleExpand = (path: string) => {
  if (expandedSections.value.has(path)) {
    expandedSections.value.delete(path);
  } else {
    expandedSections.value.add(path);
  }
};

const navigate = (path: string) => {
  router.push(path);
  emit("update:open", false);
};

const handleItemClick = (item: { path: string; children?: unknown[] }) => {
  if (item.children?.length) {
    toggleExpand(item.path);
  } else {
    navigate(item.path);
  }
};

const handleSignOut = () => {
  auth?.signOut?.value?.();
  emit("update:open", false);
};
</script>

<template>
  <PandoSheet
    :open="open"
    side="left"
    size="small"
    @update:open="emit('update:open', $event)"
  >
    <template #trigger>
      <slot name="trigger" />
    </template>

    <div class="flex h-full flex-col">
      <div class="border-b px-4 pt-4 pb-4 mb-4">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold">Aspen</h2>
        </div>
      </div>

      <nav class="flex-1 space-y-1 px-4 overflow-y-auto">
        <template v-for="(group, gi) in groupedNavigationItems" :key="gi">
          <div
            v-if="group.section"
            class="text-xs font-medium uppercase tracking-wider text-muted-foreground px-3 pt-4 pb-1"
          >
            {{ group.section }}
          </div>
          <template v-for="item in group.items" :key="item.path">
            <!-- Parent Item -->
            <button
              :class="[
                'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                isActive(item.path) && !item.children?.length
                  ? 'bg-accent text-accent-foreground border-l-2 border-primary'
                  : 'hover:bg-accent hover:text-accent-foreground',
              ]"
              @click="handleItemClick(item)"
            >
              <LucideIcon :name="item.icon" :size="20" />
              <span class="flex-1 text-left">{{ item.label }}</span>
              <LucideIcon
                v-if="item.children?.length"
                :name="isExpanded(item.path) ? 'ChevronDown' : 'ChevronRight'"
                :size="16"
                class="text-muted-foreground"
              />
            </button>

            <!-- Child Items -->
            <div
              v-if="item.children?.length && isExpanded(item.path)"
              class="ml-4 space-y-1 border-l border-border pl-3"
            >
              <button
                v-for="child in item.children.filter(
                  (c: { show?: boolean }) => c.show !== false,
                )"
                :key="child.path"
                :class="[
                  'w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors rounded-md',
                  isChildActive(child.path)
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                ]"
                @click="navigate(child.path)"
              >
                <LucideIcon :name="child.icon" :size="16" />
                <span class="truncate">{{ child.label }}</span>
              </button>
            </div>
          </template>
        </template>
      </nav>

      <div class="border-t px-4 pt-4 mt-4 space-y-3 pb-4">
        <div class="px-3 flex items-center justify-between">
          <span class="text-sm text-muted-foreground">Theme</span>
          <ThemeToggle />
        </div>

        <!-- User section (authenticated) -->
        <div v-if="auth?.userId?.value" class="flex items-center gap-3">
          <UserButton />
          <span class="text-sm text-muted-foreground">
            {{
              user?.firstName ||
              user?.primaryEmailAddress?.emailAddress?.split("@")[0] ||
              "User"
            }}
          </span>
        </div>

        <Button
          v-if="auth?.userId?.value"
          variant="ghost"
          size="sm"
          class="w-full justify-start"
          @click="handleSignOut"
        >
          <LucideIcon name="log-out" :size="16" class="mr-2" />
          Sign Out
        </Button>

        <!-- Auth buttons (unauthenticated) -->
        <template v-if="!auth?.userId?.value">
          <SignUpButton mode="modal">
            <Button
              variant="default"
              size="sm"
              class="w-full justify-center gap-2"
            >
              <LucideIcon name="user-plus" :size="16" />
              <span>Sign Up</span>
            </Button>
          </SignUpButton>
          <SignInButton mode="modal">
            <Button
              variant="outline"
              size="sm"
              class="w-full justify-center gap-2"
            >
              <LucideIcon name="log-in" :size="16" />
              <span>Sign In</span>
            </Button>
          </SignInButton>
        </template>
      </div>
    </div>
  </PandoSheet>
</template>
